import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  CHECKOUT_SESSION_KEYS,
  BIND_DECISION_MESSAGES,
} from 'V2/Checkout/constants';
import { useOrderSummary } from 'V2/Checkout/hooks/useOrderSummary';
import { usePolicyDocuments } from 'V2/Checkout/hooks/usePolicyDocuments';
import { usePaymentSession } from 'V2/Checkout/hooks/usePaymentSession';
import { useBindSubmit } from 'V2/Checkout/hooks/useBindSubmit';
import { Button } from 'V2/Shared/Button';
import { ErrorRetry } from 'V2/Shared/ErrorRetry';
import { OrderSummary } from './OrderSummary';
import { PaymentFormCard } from './PaymentFormCard';
import { AgreementSection } from './AgreementSection';
import { AdyenDropinHandle } from './CheckoutAdyen';
import styles from '../styles.module.css';

interface PaymentStepProps {
  appId: string;
  paymentPlanId: string;
  onBindDecision: (message: string) => void;
}

export const PaymentStep: React.FC<PaymentStepProps> = ({
  appId,
  paymentPlanId,
  onBindDecision,
}) => {
  const selfRef = useRef<HTMLDivElement>(null);

  const {
    data: orderData,
    isLoading: orderLoading,
    error: orderError,
    fetch: fetchRate,
  } = useOrderSummary(appId);

  const {
    documents,
    isLoading: docsLoading,
    isSigned: documentsSigned,
    isSigning,
    signError,
    fetch: fetchDocs,
    sign,
  } = usePolicyDocuments(appId, paymentPlanId);

  const {
    isReady: sessionReady,
    isLoading: sessionLoading,
    error: sessionError,
    initialize,
  } = usePaymentSession(appId);

  const { submit, bindStatus, bindErrors } = useBindSubmit(
    appId,
    paymentPlanId
  );

  const retryFetch = useCallback(async () => {
    const newQuoteId = await fetchRate();
    if (newQuoteId) {
      sessionStorage.setItem(CHECKOUT_SESSION_KEYS.quoteId, newQuoteId);
    }
    await fetchDocs();
  }, [fetchRate, fetchDocs]);

  useEffect(() => {
    setTimeout(
      () => selfRef.current?.scrollIntoView({ behavior: 'smooth' }),
      300
    );
    void initialize();
    void retryFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only — no deps needed, all callbacks are stable

  const [dueAmount, setDueAmount] = useState('');

  useEffect(() => {
    if (!orderData?.paymentDetails) return;
    const selectedId =
      orderData.paymentDetails.selectedPaymentPlan?.identifier ??
      orderData.paymentDetails.defaultPaymentPlan?.identifier;
    const plan = orderData.paymentDetails.paymentPlans?.find(
      (p) => p.identifier === selectedId
    );
    if (plan) {
      const isFullPay =
        plan.paymentFrequencyIdentifier === 'full_pay' ||
        plan.paymentFrequencyIdentifier === 'in_full';
      setDueAmount(isFullPay ? plan.totalAmount : plan.downPaymentAmount);
    }
  }, [orderData]);

  const adyenDropinRef = useRef<AdyenDropinHandle | null>(null);
  const [formValid, setFormValid] = useState(false);
  const [adyenError, setAdyenError] = useState<string | null>(null);

  const handleAdyenReady = useCallback((dropin: AdyenDropinHandle) => {
    adyenDropinRef.current = dropin;
  }, []);

  const handleConfirmPay = useCallback(() => {
    setAdyenError(null);
    adyenDropinRef.current?.submit();
  }, []);

  const handleAgreementChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      await sign(e.target.checked);
    },
    [sign]
  );

  useEffect(() => {
    const msg = BIND_DECISION_MESSAGES[bindStatus];
    if (msg) onBindDecision(msg);
  }, [bindStatus, onBindDecision]);

  const isSubmitting = bindStatus === 'submitting' || bindStatus === 'polling';

  return (
    <div ref={selfRef}>
      <OrderSummary
        data={orderData}
        isLoading={orderLoading}
        error={orderError}
        onRetry={retryFetch}
      />

      <h2 className={styles.sectionTitle}>Complete your payment</h2>

      <PaymentFormCard
        isLoading={sessionLoading}
        error={sessionError}
        isReady={sessionReady}
        onRetry={initialize}
        onAdyenReady={handleAdyenReady}
        onFormValidChange={setFormValid}
        onSubmit={submit}
      />

      <AgreementSection
        isLoading={orderLoading || docsLoading}
        isDisabled={sessionLoading || !sessionReady}
        documents={documents}
        isSigned={documentsSigned}
        isSigning={isSigning}
        signError={signError}
        onChange={handleAgreementChange}
      />

      {dueAmount && (
        <div className={styles.amountRow}>
          <span className={styles.amountLabel}>Amount due</span>
          <span className={styles.amountValue}>{dueAmount}</span>
        </div>
      )}

      {adyenError && <ErrorRetry>{adyenError}</ErrorRetry>}

      {bindErrors.map((err, i) => (
        <ErrorRetry key={i}>{err}</ErrorRetry>
      ))}

      <Button
        type="button"
        variant="contained"
        color="primary"
        width="fullWidth"
        size="lg"
        disabled={
          !formValid ||
          !documentsSigned ||
          isSigning ||
          isSubmitting ||
          !dueAmount
        }
        isSubmitting={isSubmitting}
        data-testid="confirm-pay-button"
        onClick={handleConfirmPay}
      >
        {dueAmount ? `Confirm & pay ${dueAmount}` : 'Confirm & pay'}
      </Button>
    </div>
  );
};
